import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Modal, TextInput, FormError, Button, Input } from "@components";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

const PAGES = {
  NAME: "name",
  MENU: "menu",
  DELETE: "delete",
};

const EditMenuStep = ({ onRename, onDelete }) => {
  return (
    <div className="p-5 space-y-3">
      <Button
        type="button"
        className="w-full rounded-lg justify-start"
        onClick={onRename}
      >
        Rename
      </Button>
      <Button
        type="button"
        className="w-full rounded-lg justify-start text-red-500"
        onClick={onDelete}
      >
        Delete Saved Filter
      </Button>
    </div>
  );
};

const NameStep = ({ initialName, onSave, onCancel, submitLabel = "Save" }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { name: initialName },
  });

  useEffect(() => {
    reset({ name: initialName });
  }, [initialName, reset]);

  const onSubmit = handleSubmit((data) => {
    onSave(data.name);
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="p-5 space-y-4"
    >
      <TextInput
        name="name"
        control={control}
        placeholder="Give your saved filter a name"
        required={true}
        errors={errors.name}
        displayName="Saved filter name"
        maxLength={30}
      />
      <FormError name="name" errors={errors} />
      <div className="flex gap-2">
        <Button
          type="button"
          className="shrink-0 rounded-lg"
          onClick={onCancel}
        >
          {initialName ? "Back" : "Cancel"}
        </Button>
        <Button
          type="submit"
          color="primary"
          className="shrink-0 grow rounded-lg"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

const DeleteStep = ({ viewName, onConfirm, onCancel }) => {
  const [confirmText, setConfirmText] = useState("");
  const isConfirmed = confirmText.toLowerCase() === "delete";

  return (
    <div className="p-5 space-y-4">
      <p className="text-sm text-zinc-600">
        You're about to permanently delete <strong>{viewName}</strong>. This
        action cannot be undone.
      </p>
      <div>
        <label className="block text-sm font-medium text-zinc-700 mb-1">
          Type <strong>delete</strong> to continue
        </label>
        <Input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="delete"
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          className="shrink-0 rounded-lg"
          onClick={onCancel}
        >
          Back
        </Button>
        <Button
          type="button"
          color="danger"
          className="shrink-0 grow rounded-lg"
          onClick={onConfirm}
          disabled={!isConfirmed}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

/**
 * Unified modal for creating and editing views.
 *
 * Create mode:  isOpen=true, view=null  → shows name input
 * Edit mode:    isOpen=true, view={...} → shows edit menu (rename / delete)
 */
const CreateEditViewModal = ({
  isOpen,
  onClose,
  view = null,
  onSave,
  onRename,
  onDelete,
}) => {
  const isEditMode = !!view;
  const [page, setPage] = useState(isEditMode ? PAGES.MENU : PAGES.NAME);

  // Reset page when modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      setPage(isEditMode ? PAGES.MENU : PAGES.NAME);
    }
  }, [isOpen, isEditMode]);

  const handleClose = useCallback(() => {
    setPage(PAGES.NAME);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(
    (name) => {
      if (isEditMode) {
        onRename(view, name);
      } else {
        onSave(name);
      }
      handleClose();
    },
    [isEditMode, view, onSave, onRename, handleClose],
  );

  const handleDelete = useCallback(() => {
    onDelete(view);
    handleClose();
  }, [view, onDelete, handleClose]);

  const getTitle = () => {
    switch (page) {
      case PAGES.NAME:
        return isEditMode ? "Rename Saved Filter" : "Save Filters";
      case PAGES.DELETE:
        return `Delete ${view?.name || ""} Saved Filter?`;
      case PAGES.MENU:
      default:
        return (
          <span className="flex items-center gap-2">
            <PencilSquareIcon className="size-4" /> Edit {view?.name || ""}
          </span>
        );
    }
  };

  const renderPage = () => {
    switch (page) {
      case PAGES.MENU:
        return (
          <EditMenuStep
            onRename={() => setPage(PAGES.NAME)}
            onDelete={() => setPage(PAGES.DELETE)}
          />
        );
      case PAGES.NAME:
        return (
          <NameStep
            initialName={isEditMode ? view?.name || "" : ""}
            onSave={handleSave}
            onCancel={isEditMode ? () => setPage(PAGES.MENU) : handleClose}
            submitLabel={isEditMode ? "Rename" : "Save"}
          />
        );
      case PAGES.DELETE:
        return (
          <DeleteStep
            viewName={view?.name || ""}
            onConfirm={handleDelete}
            onCancel={() => setPage(PAGES.MENU)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()} size="sm">
      {renderPage()}
    </Modal>
  );
};

export default CreateEditViewModal;
